import React from 'react';
import { useMutation } from '@apollo/client';
import I18n from 'modules/i18n';
import cn from '@/utils/cn';
import Dropdown from '@/components/common/Dropdown';
import { ISlide } from '../../stores/useSlideShowStore';
import useSlideShowContext from '../../stores/useSlideShowContext';
import { UPDATE_IMAGE_MODEL_BACKGROUND_SIZE } from '../../Utils/slideShowGraphQL';
import dropdownStyles from './BackgroundDropdownActions.module.scss';

const DEFAULT_BACKGROUND_SIZE = 'cover';

const OPTIONS = [
  { labelKey: 'cover', value: 'cover' },
  { labelKey: 'contain', value: 'contain' },
  { labelKey: 'stretch', value: '100% 100%' },
];

type Props = {
  slide: ISlide;
};

const ChangeBackgroundFit = ({ slide }: Props) => {
  const [updateImageModelMutation] = useMutation(UPDATE_IMAGE_MODEL_BACKGROUND_SIZE);
  const slideRef = useSlideShowContext(state => state.slideRef);
  const updateSlide = useSlideShowContext(state => state.updateSlide);

  if (!slideRef || !slide.imageModel) return null;

  const { imageModel } = slide;
  const currentValue = imageModel.styles?.background_size || DEFAULT_BACKGROUND_SIZE;
  const previewImage = `url(${imageModel.image?.imageUrl})`;

  const submitHandler = (backgroundSize: string) => {
    if (slideRef.current) $(slideRef.current).css({ backgroundSize });

    updateImageModelMutation({ variables: { backgroundSize, id: imageModel.id } })
      .then((res) => {
        updateSlide(slide.id, { imageModel: res.data.updateImageModel.result.imageModel });
      })
      .catch((error) => { console.error(error); });
  };

  return (
    <Dropdown
      buttonClass={`${dropdownStyles.dropdownItem} ${dropdownStyles.submenu}`}
      dropdownPosition="right"
      toggleText={(
        <>
          {I18n.t('change_fit')}
          <i className={`fa fa-angle-right ${dropdownStyles.angleRight}`} />
        </>
      )}
    >
      {OPTIONS.map(option => (
        <button
          key={option.value}
          className={cn(
            'tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-4 tw-py-2 tw-text-left tw-cursor-pointer',
            'tw-bg-inherit tw-border-none tw-font-normal tw-leading-normal tw-text-[#555]',
            'hover:tw-bg-[#eee] hover:tw-text-[#484848]',
          )}
          type="button"
          onClick={() => submitHandler(option.value)}
        >
          {/* 64x36 keeps the slide's native 842:474 aspect ratio so the preview matches the canvas. */}
          <span
            aria-hidden="true"
            className={cn(
              'tw-flex-shrink-0 tw-w-16 tw-h-9 tw-bg-[#e5e5e5] tw-bg-center tw-bg-no-repeat',
              'tw-border tw-border-[#ccc] tw-rounded-sm',
            )}
            style={{ backgroundImage: previewImage, backgroundSize: option.value }}
          />
          <span className="tw-flex-grow">{I18n.t(option.labelKey)}</span>
          {option.value === currentValue && <i className="fa fa-check tw-text-tci-purple" />}
        </button>
      ))}
    </Dropdown>
  );
};

export default ChangeBackgroundFit;
